import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

// Manifest report shaping + workbook build for the public download page.
// Mirrors fullwidget/src/components/AdminActivityManifest/manifestReport.ts and
// manifestWorkbook.ts — the two frontends both render the emailed link, so both
// carry this until the legacy app is retired. Keep them in step.

// Trailing customer-type tokens this client's tickets are named with, e.g.
// "Standard Seating Adult" -> group "Standard Seating", type "Adult".
// Longest-first so "Employee - Adult" matches before a bare "Adult".
const CUSTOMER_TYPE_SUFFIXES = [
    'Employee - Adult',
    'Employee - Child',
    'Hotel Comp',
    'Adult',
    'Child',
    'Toddler',
    'Infant',
    'Senior'
];

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const NO_SEAT = '-';

function splitSeatingGroup(ticketName) {
    const name = ticketName || '';
    const suffix = CUSTOMER_TYPE_SUFFIXES.find(
        s => name.endsWith(s) && name.slice(0, name.length - s.length).trim()
    );
    if (!suffix) {
        return { group: name, type: name };
    }
    return { group: name.slice(0, name.length - suffix.length).trim(), type: suffix };
}

function displayValue(value) {
    if (value == null) {
        return '';
    }
    if (Array.isArray(value)) {
        return value.map(String).join(', ');
    }
    if (typeof value === 'object') {
        const direct = value.name;
        if (direct) {
            return String(direct);
        }
        const nested = Object.values(value).find(v => v && typeof v === 'object' && v.name);
        return nested ? String(nested.name) : JSON.stringify(value);
    }
    return String(value);
}

// One booking's answers, flattened across its ticket types. The backend already
// merged each ticket's booker + guest answers; the only collision left is the
// same field name under two ticket types — keep both rather than overwriting.
function mergeBookingFields(guest) {
    const merged = new Map();
    (guest.tickets || []).forEach(ticket => {
        Object.entries(ticket.customFields || {}).forEach(([name, raw]) => {
            const value = displayValue(raw);
            if (!value) {
                return;
            }
            const seen = merged.get(name);
            if (seen === undefined) {
                merged.set(name, value);
            } else if (seen !== value) {
                merged.set(name, `${seen}\n${value}`);
            }
        });
    });
    return merged;
}

// M/D/YYYY (unpadded), read back with UTC getters.
function reportDate(value) {
    if (!value) {
        return '';
    }
    const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
        ? `${value.replace(' ', 'T')}Z`
        : value;
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return '';
    }
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

// A single-timeslot activity's start and end usually fall on the same day, so
// collapse the range unless the timeslot runs into the next one.
function dateRangeLabel(start, end) {
    const from = reportDate(start);
    const to = reportDate(end) || from;
    return from === to ? from : `${from} - ${to}`;
}

// bookingDate arrives as region wall-clock "YYYY-MM-DD HH:mm:ss" — print the
// digits as-is (MM/DD/YY, h:mm am), same as fullwidget's formatApiDateTime.
function bookedAtLabel(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value || '');
    if (!m) return '';
    const hours = Number(m[4]);
    const ampm = hours >= 12 ? 'pm' : 'am';
    return `${m[2]}/${m[3]}/${m[1].slice(-2)}, ${hours % 12 || 12}:${m[5]} ${ampm}`;
}

function collectSeatingGroups(guests) {
    const groups = new Map();
    guests.forEach(guest => {
        (guest.tickets || []).forEach(ticket => {
            const { group, type } = splitSeatingGroup(ticket.name || '');
            if (!groups.has(group)) {
                groups.set(group, { bookings: new Set(), pax: 0, byType: new Map() });
            }
            const g = groups.get(group);
            const quantity = ticket.quantity || 0;
            g.bookings.add(guest.reservationId);
            g.pax += quantity;
            g.byType.set(type, (g.byType.get(type) || 0) + quantity);
        });
    });
    return Array.from(groups.entries()).map(([name, g]) => ({
        name,
        bookings: g.bookings.size,
        pax: g.pax,
        paxBreakdown: Array.from(g.byType.entries()).map(([type, count]) => ({ type, count })),
        // Nothing checks guests in yet; the columns exist so the sheet's shape
        // doesn't change once that lands.
        checkedIn: 0,
        notCheckedIn: g.bookings.size
    }));
}

export function buildManifestReport(activityStart, activityEnd, guests) {
    const fieldsByBooking = guests.map(mergeBookingFields);

    // Only the custom fields some booking actually answered become columns.
    const customFieldNames = [];
    fieldsByBooking.forEach(fields => {
        Array.from(fields.keys()).forEach(name => {
            if (!customFieldNames.includes(name)) {
                customFieldNames.push(name);
            }
        });
    });

    const rows = guests.map((guest, i) => ({
        bookingId: guest.reservationId,
        bookingStatus: guest.bookingStatus || '',
        contact: guest.contactName || '',
        voucher: guest.confirmationCode || '',
        customers: (guest.tickets || []).map(t => `${t.quantity} ${t.name}`).join(', '),
        seatingGroup: Array.from(
            new Set((guest.tickets || []).map(t => splitSeatingGroup(t.name || '').group))
        ).join(', '),
        customFieldValues: customFieldNames.map(name => fieldsByBooking[i].get(name) || '')
    }));

    return {
        dateRangeLabel: dateRangeLabel(activityStart, activityEnd),
        totalRecords: rows.length,
        seatingGroups: collectSeatingGroups(guests),
        customFieldNames,
        rows
    };
}

export function manifestSheetRows(report) {
    return [
        ['Manifest Report'],
        [`Date Range: ${report.dateRangeLabel}`],
        [`Total Records: ${report.totalRecords}`],
        [],
        ...report.seatingGroups.map(g => [
            `${g.name}: ${g.bookings} bookings, ${g.pax} pax — ` +
                `${g.paxBreakdown.map(b => `${b.count} ${b.type}`).join(', ')} — ` +
                `${g.checkedIn} checked in, ${g.notCheckedIn} not checked in`
        ]),
        [],
        [
            'Booking ID',
            'Booking Status',
            'Contact',
            'Voucher',
            'Booking Date',
            'Customers',
            'Checked In',
            'Seating Group',
            'Seats',
            ...report.customFieldNames
        ],
        ...report.rows.map(r => [
            r.bookingId,
            r.bookingStatus,
            r.contact,
            r.voucher,
            r.bookedAt,
            r.customers,
            // Check-in and seat assignment don't exist yet; the reference report
            // shows both columns, so they ship as fixed placeholders.
            'No',
            r.seatingGroup,
            NO_SEAT,
            ...r.customFieldValues
        ])
    ];
}

function sanitizeFilenamePart(part) {
    return String(part)
        .replace(/[^a-zA-Z0-9-_]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function downloadManifestWorkbook(report, filenameSuffix) {
    const sheet = XLSX.utils.aoa_to_sheet(manifestSheetRows(report));
    const workbook = { Sheets: { 'Manifest Report': sheet }, SheetNames: ['Manifest Report'] };
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(
        new Blob([buffer], { type: XLSX_MIME }),
        `manifest-${sanitizeFilenamePart(filenameSuffix)}.xlsx`
    );
}
